import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, SESSION_TOKEN_KEY } from "@/App";

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);

        if (!sessionIdMatch) {
          console.error("No session_id found in URL");
          navigate("/login");
          return;
        }

        const sessionId = sessionIdMatch[1];

        // Exchange session_id for session_token
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        if (response.data) {
          // Safari/ITP-safe: persist the session_token in localStorage so the
          // axios interceptor can send Authorization: Bearer on every request,
          // independent of whether Safari kept the cookie.
          if (response.data.session_token) {
            localStorage.setItem(SESSION_TOKEN_KEY, response.data.session_token);
          }
          // Beta gate routing — non-allowlisted users land on the request
          // access page (where they can redeem an invite code). Owners and
          // approved testers go straight to the dashboard.
          window.history.replaceState(null, "", window.location.pathname);
          if (response.data.allowed === false) {
            navigate("/beta/access", { replace: true, state: { user: response.data } });
          } else {
            navigate("/dashboard", { replace: true, state: { user: response.data } });
          }
        }
      } catch (error) {
        console.error("Auth callback error:", error);
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-muted-foreground text-sm">Authenticating...</p>
      </div>
    </div>
  );
}
